import React from 'react'

import { TextClamp } from '../components/text/TextClamp'
import { sanitizeNarrativeText } from '../foundations/text-sanitize'
import { StageLayout } from '../layouts/StageLayout'
import { PriceCurvesPlot } from '../plots/price-curves/PriceCurvesPlot'
import { readPriceSensitivitySlide } from '../plots/price-curves/adapters'
import { registerSlide } from './registry'
import { SlotFootnote } from '../slots'
import type { EditableSlideProps } from './contracts'
import type { PriceSensitivitySlideData } from './renderer-internal-types'

// The slide's geometry helpers live with the plot's slide view now (plots
// wave 7, D2); re-exported so their callers keep one import path.
export { axisTicks, formatPrice, layoutTopLabelX, priceToX, shareToY } from '../plots/price-curves/PriceCurvesSlideView'

/**
 * PriceSensitivitySlide - the joint Van Westendorp slide (D4).
 *
 * Frozen from the PT2 mock that passed the Wave 0 visual gate. The one rule
 * that is not cosmetic: GEOMETRY IS EXACT, LABELS ARE ROUNDED (D1). Every mark
 * on the plot is positioned from `band`, `optimumPrice`, `reference.value` and
 * `displayWindow`, which are the exact interpolated crossings; every number a
 * reader sees comes from `reported`, which the producer rounded. On the
 * reference study the two are 9 to 14px apart at slide scale and nothing is
 * drawn at the rounded price, so a mark placed on `reported` points at a price
 * the curves never meet at.
 *
 * Below the thin base the producer withholds `reported.point` and this
 * component withholds it everywhere at once (D5): no marker, no tile value, no
 * number in any copy. The optimum tile then states the absence rather than
 * quietly disappearing.
 *
 * The chart is the shared `price-curves` plot at slide size (plots wave 7,
 * D2): a slide built since then draws its stamped `plot`; a slide stored
 * before it is read by `priceSensitivitySlideAdapter` from its own fields.
 * The slide keeps its frame and footer. Every visible word arrives
 * pre-translated (deck-ui has no translation runtime); the English defaults
 * below are the last resort for a deck stored before a label existed, not the
 * normal path.
 *
 * Colors are deck-ui `--slide-*` tokens, which are defined once on `:root` in
 * this package's own `tokens.css`. That is deliberate: this slide renders in
 * the frontend app, in the `/internal/deck-render` export route and in
 * `apps/backoffice/web`, and the last of those defines no app-level chart
 * tokens at all. There is no dark deck theme to style for.
 */
export interface PriceSensitivitySlideProps
  extends PriceSensitivitySlideData,
    EditableSlideProps {}

const GRAY = 'var(--slide-text-muted, #737373)'

// legacy reader only: the builder stamps every one of these in `displayLabels`.
const DEFAULT_LABELS = {
  rangeLabel: 'Acceptable price range',
  rangeSeparator: 'to',
  optimumLabel: 'Optimal price point',
  pointWithheldLabel: 'Not published at this base',
  lowerEndpointLabel: 'Lower acceptable price',
  upperEndpointLabel: 'Upper acceptable price',
  referenceLabel: 'Typical current spend',
  referenceBaseLabel: '',
  yAxisTitle: 'Share of the base',
  xAxisTitle: 'Price',
}

function PriceSensitivitySlideComponent(props: PriceSensitivitySlideProps) {
  const { editable, onEdit } = props
  // English defaults are the last resort of a deck stored before a label
  // existed (legacy reader only): a stamped plot carries every word it draws.
  const labels = { ...DEFAULT_LABELS, ...(props.displayLabels || {}) }
  const { payload } = readPriceSensitivitySlide({ ...props, displayLabels: labels })

  return (
    <StageLayout
      preset="price-sensitivity"
      eyebrowText={props.eyebrow}
      // The shipped default is `uppercase`, which is the standing no-uppercase-
      // tracked-labels rule broken by a default rather than by this slide.
      eyebrowTextTransform="none"
      clientLogoUrl={props.clientLogoUrl ?? undefined}
      footerText={props.footerText ?? undefined}
      actionTitle={sanitizeNarrativeText(props.actionTitle)}
      actionSubtitle={props.actionSubtitle}
      editable={editable}
      onEdit={onEdit}
    >
      <div
        data-testid="price-sensitivity-layout"
        style={{ display: 'flex', flexDirection: 'column', minHeight: 0, flex: 1 }}
      >
        <PriceCurvesPlot
          data={payload.data}
          props={{ size: 'slide' }}
          xAxisTitle={labels.xAxisTitle}
          testIdPrefix="price-sensitivity"
        />

        {/* One running footer, base and provenance grounding the numbers
            first, then the method caveat, not two stacked captions competing
            for the reader's attention. Both source strings keep their own
            testid so existing assertions on each string still resolve; only
            the visual grouping and reading order change. */}
        <SlotFootnote testId="price-sensitivity-footer" maxLines={3} lineHeight="1.35" color={GRAY} style={{ marginTop: 12, flexShrink: 0 }} renderMode={props.renderMode}>
          <span data-testid="price-sensitivity-base-line">{props.baseLine}</span>{' '}
          <span data-testid="price-sensitivity-method-note">{props.methodNote}</span>
        </SlotFootnote>
      </div>
    </StageLayout>
  )
}

export const PriceSensitivitySlide = registerSlide(
  'price-sensitivity',
  PriceSensitivitySlideComponent,
)
